import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import ChatService from 'services/chat';
import config from 'utils/config';

interface ChatRoom {
  id: string;
  customer: {
    id: string;
    name: string;
    email: string;
  };
  shop: {
    id: string;
    name: string;
  };
  booking?: {
    id: string;
  };
  type: string;
  status: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount?: {
    shop: number;
    customer: number;
  };
  metadata?: any;
  updatedAt: string;
  createdAt: string;
}

interface Message {
  id: string;
  content: string;
  sender: string;
  timestamp: Date | string;
  status: string;
  senderName?: string;
  senderAvatar?: string;
  senderId: string;
  isRead?: boolean;
  createdAt?: string;
}

interface Conversation {
  id: string;
  lastMessage?: Message;
  updatedAt: string;
  unreadCount?: number;
}

interface ChatState {
  chatRooms: ChatRoom[];
  conversations: Conversation[];
  activeConversation: Conversation | null;
  currentChatRoom: ChatRoom | null; // For header display
  messages: Map<string, Message[]>;
  unreadCounts: Map<string, number>;
  totalUnreadCount: number;
  isLoading: boolean;
  isLoadingMessages: boolean;
  error: string | null;
  pagination: {
    totalDocs: number;
    totalPages: number;
    page: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null;

  // Socket-related state
  connectionStatus: 'connected' | 'connecting' | 'disconnected';
  typingUsers: Map<string, Set<string>>; // roomId -> Set of userIds
  onlineUsers: Set<string>;

  // Actions
  initializeOrGetChatRoom: (userId: string, shopId: string) => Promise<ChatRoom | null>;
  loadChatRooms: (shopId?: string, page?: number) => Promise<void>;
  loadConversations: () => Promise<void>;
  loadMessages: (conversationId: string, page?: number) => Promise<void>;
  sendMessage: (conversationId: string, text: string) => Promise<void>;
  markConversationAsRead: (conversationId: string) => Promise<void>;
  setActiveConversation: (conversation: Conversation | null) => void;
  setCurrentChatRoom: (room: ChatRoom | null) => void;
  addMessage: (conversationId: string, message: Message) => void;
  updateConversation: (conversation: Conversation) => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  clearError: () => void;

  // Socket-related actions
  setConnectionStatus: (status: 'connected' | 'connecting' | 'disconnected') => void;
  addTypingUser: (roomId: string, userId: string) => void;
  removeTypingUser: (roomId: string, userId: string) => void;
  setUserOnline: (userId: string, isOnline: boolean) => void;
  handleRealtimeMessage: (roomId: string, message: Message) => void;
}

export const useChatStore = create<ChatState>()(
  devtools(
    persist(
      (set, get) => ({
        chatRooms: [],
        conversations: [],
        activeConversation: null,
        currentChatRoom: null,
        messages: new Map(),
        unreadCounts: new Map(),
        totalUnreadCount: 0,
        isLoading: false,
        isLoadingMessages: false,
        error: null,
        pagination: null,

        // Socket-related state
        connectionStatus: 'disconnected',
        typingUsers: new Map(),
        onlineUsers: new Set(),

        initializeOrGetChatRoom: async (userId: string, shopId: string) => {

          // Check if token exists
          const config = (await import('utils/config')).default;
          const token = config.get('token');

          set({ isLoading: true, error: null });
          try {
            const response = await ChatService.initializeOrGetChatRoom(userId, shopId);

            const chatRoom = response?.data;

            if (!chatRoom) {
              throw new Error('Failed to initialize chat room');
            }

            // Update chat rooms list if not already present
            set((state) => {
              const existingIndex = state.chatRooms.findIndex(room => room.id === chatRoom.id);
              const updatedRooms = existingIndex >= 0
                ? state.chatRooms.map((room, index) => index === existingIndex ? chatRoom : room)
                : [...state.chatRooms, chatRoom];

              return {
                chatRooms: updatedRooms,
                isLoading: false,
              };
            });

            return chatRoom;
          } catch (error: any) {
            set({
              error: error.message || 'Failed to initialize chat room',
              isLoading: false,
            });
            console.error('Error initializing chat room:', error);
            return null;
          }
        },

        loadChatRooms: async (shopId?: string, page = 1) => {
          set({ isLoading: true, error: null });
          try {
            const response = await ChatService.getChatRooms({
              page,
              limit: 20,
              where: shopId ? { shop: { equals: shopId } } : undefined,
            });

            const chatRooms = (response?.data || []) as ChatRoom[];
            const params = response?.params || {};

            // Calculate total unread count
            let totalUnreadCount = 0;
            const unreadCounts = new Map<string, number>();

            chatRooms.forEach((room) => {
              const unread = room.unreadCount?.shop || 0;
              unreadCounts.set(room.id, unread);
              totalUnreadCount += unread;
            });

            set({
              chatRooms,
              unreadCounts,
              totalUnreadCount,
              pagination: {
                totalDocs: params.totalDocs || 0,
                totalPages: params.totalPages || 0,
                page: params.page || 1,
                limit: params.limit || 20,
                hasNextPage: params.hasNextPage || false,
                hasPrevPage: params.hasPrevPage || false,
              },
              isLoading: false,
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load chat rooms',
              isLoading: false,
            });
            console.error('Error loading chat rooms:', error);
          }
        },

        loadConversations: async () => {
          set({ isLoading: true, error: null });
          try {
            // TODO: Implement getConversations method in ChatService
            // const response = await ChatService.getConversations({
            //   page: 1,
            //   limit: 50,
            //   orderBy: { updatedAt: 'desc' },
            //   fetchPolicy: 'cache-first',
            // });
            const response = { data: [] };

            const conversations = (response?.data || []) as Conversation[];

            // Calculate unread counts
            const unreadCounts = new Map<string, number>();
            let totalUnreadCount = 0;

            conversations.forEach((conv) => {
              const unread = conv.unreadCount || 0;
              unreadCounts.set(conv.id, unread);
              totalUnreadCount += unread;
            });

            set({
              conversations,
              unreadCounts,
              totalUnreadCount,
              isLoading: false,
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load conversations',
              isLoading: false,
            });
            console.error('Error loading conversations:', error);
          }
        },

        loadMessages: async (conversationId, page = 1) => {
          set({ isLoadingMessages: true, error: null });
          try {
            const response = await ChatService.getMessages({
              roomId: conversationId,
              page,
              limit: 50,
            });

            const newMessages = (response?.data || []) as Message[];

            set((state) => {
              const existingMessages = state.messages.get(conversationId) || [];
              const messages = new Map(state.messages);

              // Merge messages (for pagination)
              if (page === 1) {
                messages.set(conversationId, newMessages);
              } else {
                // Append older messages
                messages.set(conversationId, [...existingMessages, ...newMessages]);
              }

              return {
                messages,
                isLoadingMessages: false,
              };
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load messages',
              isLoadingMessages: false,
            });
            console.error('Error loading messages:', error);
          }
        },

        sendMessage: async (conversationId, text) => {
          set({ error: null });
          try {
            const response = await ChatService.sendMessage({
              roomId: conversationId,
              content: text,
            });

            const newMessage = response?.data as Message;

            if (newMessage) {
              get().addMessage(conversationId, newMessage);

              // Update conversation's last message
              set((state) => ({
                conversations: state.conversations.map((conv) =>
                  conv.id === conversationId
                    ? { ...conv, lastMessage: newMessage, updatedAt: newMessage.createdAt || new Date().toISOString() }
                    : conv
                ),
              }));
            }
          } catch (error: any) {
            set({
              error: error.message || 'Failed to send message',
            });
            throw error;
          }
        },

        markConversationAsRead: async (conversationId) => {
          set({ error: null });
          try {
            // TODO: Implement markAsRead method in ChatService
            // await ChatService.markAsRead({ conversationId });

            set((state) => {
              const unreadCounts = new Map(state.unreadCounts);
              const previousUnread = unreadCounts.get(conversationId) || 0;
              unreadCounts.set(conversationId, 0);

              return {
                unreadCounts,
                totalUnreadCount: Math.max(0, state.totalUnreadCount - previousUnread),
                conversations: state.conversations.map((conv) =>
                  conv.id === conversationId
                    ? { ...conv, unreadCount: 0 }
                    : conv
                ),
              };
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to mark conversation as read',
            });
            throw error;
          }
        },

        setActiveConversation: (conversation) => {
          set({ activeConversation: conversation });

          // Mark as read when setting active
          if (conversation) {
            get().markConversationAsRead(conversation.id);
          }
        },

        setCurrentChatRoom: (room) => {
          set({ currentChatRoom: room });
        },

        addMessage: (conversationId, message) => {
          set((state) => {
            const messages = new Map(state.messages);
            const existingMessages = messages.get(conversationId) || [];
            messages.set(conversationId, [message, ...existingMessages]);

            // Update unread count if message is from another user
            const unreadCounts = new Map(state.unreadCounts);
            if (!message.isRead && message.senderId !== 'currentUserId') {
              const currentUnread = unreadCounts.get(conversationId) || 0;
              unreadCounts.set(conversationId, currentUnread + 1);
            }

            return {
              messages,
              unreadCounts,
              totalUnreadCount: Array.from(unreadCounts.values()).reduce(
                (sum, count) => sum + count,
                0
              ),
            };
          });
        },

        updateConversation: (conversation) => {
          set((state) => ({
            conversations: state.conversations.map((conv) =>
              conv.id === conversation.id ? conversation : conv
            ),
            activeConversation:
              state.activeConversation?.id === conversation.id
                ? conversation
                : state.activeConversation,
          }));
        },

        deleteConversation: async (conversationId) => {
          set({ error: null });
          try {
            // TODO: Implement deleteConversation method in ChatService
            // await ChatService.deleteConversation({ id: conversationId });

            set((state) => {
              const messages = new Map(state.messages);
              messages.delete(conversationId);

              const unreadCounts = new Map(state.unreadCounts);
              const deletedUnread = unreadCounts.get(conversationId) || 0;
              unreadCounts.delete(conversationId);

              return {
                conversations: state.conversations.filter(
                  (conv) => conv.id !== conversationId
                ),
                messages,
                unreadCounts,
                totalUnreadCount: Math.max(0, state.totalUnreadCount - deletedUnread),
                activeConversation:
                  state.activeConversation?.id === conversationId
                    ? null
                    : state.activeConversation,
              };
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to delete conversation',
            });
            throw error;
          }
        },

        clearError: () => {
          set({ error: null });
        },

        // Socket-related actions
        setConnectionStatus: (status) => set({ connectionStatus: status }),

        addTypingUser: (roomId, userId) => {
          set((state) => {
            const typingUsers = new Map(state.typingUsers);
            const roomTyping = typingUsers.get(roomId) || new Set();
            roomTyping.add(userId);
            typingUsers.set(roomId, roomTyping);
            return { typingUsers };
          });
        },

        removeTypingUser: (roomId, userId) => {
          set((state) => {
            const typingUsers = new Map(state.typingUsers);
            const roomTyping = typingUsers.get(roomId);
            if (roomTyping) {
              roomTyping.delete(userId);
              if (roomTyping.size === 0) {
                typingUsers.delete(roomId);
              } else {
                typingUsers.set(roomId, roomTyping);
              }
            }
            return { typingUsers };
          });
        },

        setUserOnline: (userId, isOnline) => {
          set((state) => {
            const onlineUsers = new Set(state.onlineUsers);
            if (isOnline) {
              onlineUsers.add(userId);
            } else {
              onlineUsers.delete(userId);
            }
            return { onlineUsers };
          });
        },

        handleRealtimeMessage: (roomId, message) => {
          set((state) => {
            const messages = new Map(state.messages);
            const roomMessages = messages.get(roomId) || [];

            // Check for duplicate
            const exists = roomMessages.some(m => m.id === message.id);
            if (!exists) {
              messages.set(roomId, [...roomMessages, message]);
            }

            return { messages };
          });
        },
      }),
      {
        name: 'chat-storage',
        partialize: (state) => ({
          // Only persist limited data
          conversations: state.conversations.slice(0, 20),
          totalUnreadCount: state.totalUnreadCount,
        }),
      }
    ),
    { name: 'chat-store' }
  )
);